import {
  AfterViewInit,
  Component,
  ElementRef,
  PLATFORM_ID,
  ViewChild,
  computed,
  inject,
  signal,
} from '@angular/core';
import { isPlatformBrowser, NgTemplateOutlet } from '@angular/common';
import { Router } from '@angular/router';
import { Store } from '@ngxs/store';
import { Subject, of } from 'rxjs';
import { catchError, debounceTime, map, switchMap, tap } from 'rxjs/operators';
import {
  CampaignState,
  CharityState,
  GetCharitiesForSearch,
  SearchEntities,
  SearchResult,
} from '@app/state';
import { AppRoutes } from '@app/core/routing';
import { searchResultRoute } from '@app/core/routing/search-result-route';
import {
  AvatarComponent,
  BadgeComponent,
  ButtonComponent,
  DaisyDialogRef,
  IconComponent,
  IconName,
} from '@app/ui';
import { UntilDestroy, untilDestroyed } from '@ngneat/until-destroy';

const MIN_CHARS = 2;
const DEBOUNCE_MS = 250;

@UntilDestroy()
@Component({
  selector: 'gb-header-search-dialog',
  standalone: true,
  imports: [NgTemplateOutlet, IconComponent, ButtonComponent, AvatarComponent, BadgeComponent],
  templateUrl: './header-search-dialog.component.html',
  styleUrls: ['./header-search-dialog.component.scss'],
})
export class HeaderSearchDialogComponent implements AfterViewInit {
  @ViewChild('searchInput') private searchInputRef?: ElementRef<HTMLInputElement>;

  private readonly store = inject(Store);
  private readonly router = inject(Router);
  private readonly dialogRef = inject(DaisyDialogRef);
  private readonly isBrowser = isPlatformBrowser(inject(PLATFORM_ID));

  private readonly terms = new Subject<string>();

  readonly query = signal('');
  readonly searching = signal(false);
  readonly hasError = signal(false);
  readonly results = signal<SearchResult[]>([]);
  readonly recent = this.store.selectSignal(CampaignState.recentSearches);
  readonly popularCharities = this.store.selectSignal(CharityState.getSearchCharity);
  readonly charitiesLoading = signal(true);

  /** Varied title widths so the loading rows read as a list rather than a block. */
  readonly skeletonRowWidths = ['80%', '60%', '100%', '66%', '75%'];

  readonly popularCharityItems = computed(() => {
    const charities = this.popularCharities()?.charityList;
    if (!charities?.length) return [];
    return charities.map((charity) => ({
      name: charity.name,
      thumbnail: charity.logo?.color,
      slug: charity.slug,
    }));
  });

  readonly quickLinks: { label: string; icon: IconName; path: string }[] = [
    { label: 'Browse campaigns', icon: 'compass', path: AppRoutes.discover() },
    { label: 'How it works', icon: 'info', path: AppRoutes.platform.howItWorks() },
    { label: 'Start fundraising', icon: 'sparkles', path: AppRoutes.create.campaign() },
    { label: 'Help center', icon: 'circle-help', path: AppRoutes.page.support() },
  ];

  readonly showEmptyState = computed(
    () =>
      this.query().trim().length >= MIN_CHARS &&
      !this.searching() &&
      !this.hasError() &&
      !this.results().length
  );

  constructor() {
    if (this.isBrowser) {
      this.store
        .dispatch(new GetCharitiesForSearch('', '', 1, 4, 1))
        .pipe(untilDestroyed(this))
        .subscribe({
          next: () => this.charitiesLoading.set(false),
          error: () => this.charitiesLoading.set(false),
        });
    }

    this.terms
      .pipe(
        debounceTime(DEBOUNCE_MS),
        map((query) => query.trim()),
        tap((query) => {
          this.hasError.set(false);
          this.searching.set(query.length >= MIN_CHARS);
        }),
        switchMap((query) => {
          if (query.length < MIN_CHARS) return of<SearchResult[]>([]);

          return this.store.dispatch(new SearchEntities(query)).pipe(
            map(() => this.store.selectSnapshot(CampaignState.searchResults)),
            catchError(() => {
              this.hasError.set(true);
              return of<SearchResult[]>([]);
            })
          );
        }),
        untilDestroyed(this)
      )
      .subscribe((results) => {
        this.results.set(results);
        this.searching.set(false);
      });
  }

  ngAfterViewInit(): void {
    if (this.isBrowser) {
      setTimeout(() => this.searchInputRef?.nativeElement.focus());
    }
  }

  onSearch(value: string): void {
    this.query.set(value);
    this.terms.next(value);
  }

  searchAgain(term: string): void {
    this.onSearch(term);
    this.searchInputRef?.nativeElement.focus();
  }

  clearSearch(): void {
    this.searchAgain('');
  }

  retrySearch(): void {
    this.terms.next(this.query());
  }

  onKeydown(e: KeyboardEvent): void {
    switch (e.key) {
      case 'Enter':
        if (this.query().trim().length >= MIN_CHARS) {
          this.discoverWith(this.query().trim());
        }
        break;
      case 'Escape':
        this.dialogRef.dismiss();
        break;
    }
  }

  goTo(path: string): void {
    this.dialogRef.dismiss();
    void this.router.navigateByUrl(path);
  }

  goToResult(result: SearchResult): void {
    this.goTo(searchResultRoute(result));
  }

  discoverWith(searchQuery: string): void {
    this.dialogRef.dismiss();
    this.router.navigate([AppRoutes.discover()], {
      queryParams: { q: searchQuery },
    });
  }
}
